/**
 * Semantic status colours for the validation and knowledge-base surfaces.
 *
 * These are NOT blueprint chrome. Blueprint (`./blueprintTheme.js`) styles the
 * instrument surfaces — panels, rows, controls, edges — in maroon and dark ink.
 * This file covers the orthogonal axis: what a piece of content *means*.
 * A preflight row that failed is red because it failed, not because of branding.
 *
 * Why these stay distinct hues: the validation tables encode pass / warn / fail
 * / informational state, and a reader has to tell them apart at a glance. If
 * every state were a maroon tint the distinction would collapse. Colour is
 * never the only signal though — every use here is paired with an icon or a
 * text label.
 *
 * Replaces 56 ad-hoc Tailwind-default hex literals that were scattered across
 * inline styles in App.jsx. Each family has the same five slots so a swap is
 * mechanical.
 *
 *   bg          faint tint for a callout or row background
 *   bgStrong    the same tint, one step up, for emphasis or a header row
 *   border      hairline that reads against `bg`
 *   solid       the full-strength hue — icons, dots, bars. Not small text.
 *   text        readable on `bg` and on white; all are >= 6:1 on white
 *   textStrong  the darkest step, for a heading inside a callout
 */

export const STATUS = {
  success: {
    bg: '#ecfdf5',
    bgStrong: '#d1fae5',
    border: '#6ee7b7',
    solid: '#059669',
    text: '#065f46',
    textStrong: '#14532d',
  },
  warning: {
    bg: '#fffbeb',
    bgStrong: '#fef3c7',
    border: '#fde68a',
    solid: '#f59e0b',
    text: '#92400e',
    textStrong: '#78350f',
  },
  danger: {
    bg: '#fee2e2',
    bgStrong: '#fecaca',
    border: '#fca5a5',
    solid: '#dc2626',
    text: '#991b1b',
    textStrong: '#7f1d1d',
  },
  info: {
    bg: '#eff6ff',
    bgStrong: '#dbeafe',
    border: '#bfdbfe',
    solid: '#2563eb',
    text: '#1d4ed8',
    textStrong: '#1e3a8a',
  },
  /** Table chrome: zebra rows, cell borders, and the body/secondary text
   *  inside a data table. Matches the dark-ink ramp in blueprintTheme. */
  neutral: {
    surface: '#f9fafb',
    surfaceAlt: '#f3f4f6',
    border: '#e5e7eb',
    borderStrong: '#cbd5e1',
    textMuted: '#6b7280',
    text: '#374151',
    textStrong: '#1f2937',
  },
};

export default STATUS;
